import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";
import { EyeIcon, EyeOffIcon } from "../components/icons";
import { Spinner } from "../components/ui";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      await login(form.email.trim(), form.password);
      navigate("/", { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.message || "Invalid email or password.";
      // Inline *and* toast: the toast is transient, but a field-crew member
      // squinting at a phone in daylight needs the reason to stay on screen.
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const field =
    "h-12 w-full rounded-xl border border-slate-300 bg-white px-4 text-slate-900 placeholder-slate-400 " +
    "transition-shadow focus:border-brand-600 focus:outline-none focus:ring-4 focus:ring-brand-600/15";

  return (
    <div className="relative flex min-h-dvh flex-col overflow-hidden bg-slate-50">
      {/* Brand wash behind the top of the page */}
      <div
        aria-hidden="true"
        className="absolute inset-x-0 top-0 h-64 bg-gradient-to-br from-brand-700 to-brand-900"
      />

      <div className="relative flex flex-1 flex-col items-center justify-center px-5 py-10 pt-safe pb-safe sm:px-6">
        <div className="w-full max-w-sm">
          {/* Brand */}
          <div className="mb-7 text-center">
            <img
              src="/logo.png"
              alt="HILAND Survey"
              className="mx-auto h-16 w-auto object-contain sm:h-20"
            />
            {/* brand-100 on brand-700/900 — high contrast, unlike brand-200 */}
            <p className="mt-3 font-display text-sm font-medium tracking-wide text-brand-100">
              Crew Portal
            </p>
          </div>

          {/* Card */}
          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-lift sm:p-7">
            <h1 className="font-display text-xl font-bold text-slate-900">Sign in</h1>
            <p className="mt-1 text-sm text-slate-500">
              Use the credentials issued by your supervisor.
            </p>

            <form onSubmit={handleSubmit} className="mt-6 space-y-4" noValidate>
              <div>
                <label
                  htmlFor="email"
                  className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600"
                >
                  Email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  inputMode="email"
                  required
                  placeholder="you@hilandsurvey.com"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className={field}
                  autoComplete="email"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck="false"
                  aria-invalid={!!error}
                />
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600"
                >
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPwd ? "text" : "password"}
                    required
                    placeholder="Your password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className={`${field} pr-14`}
                    autoComplete="current-password"
                    aria-invalid={!!error}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((v) => !v)}
                    aria-label={showPwd ? "Hide password" : "Show password"}
                    aria-pressed={showPwd}
                    className="tap absolute right-0.5 top-1/2 flex -translate-y-1/2 items-center justify-center rounded-xl text-slate-400 transition-colors hover:text-slate-700"
                  >
                    {showPwd ? <EyeOffIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              {error && (
                <p role="alert" className="rounded-xl bg-red-50 px-3.5 py-2.5 text-sm text-red-700">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="flex min-h-[52px] w-full items-center justify-center gap-2.5 rounded-xl bg-brand-700 px-4 font-display text-base font-semibold text-white shadow-sm transition-colors hover:bg-brand-800 active:scale-[0.99] disabled:cursor-not-allowed disabled:bg-brand-700/60 disabled:active:scale-100"
              >
                {loading && <Spinner className="h-4 w-4" />}
                {loading ? "Signing in…" : "Sign in"}
              </button>
            </form>
          </div>

          <p className="mt-7 text-center text-xs text-slate-500">
            HILAND Survey · Crew Portal · v2.0
          </p>
        </div>
      </div>
    </div>
  );
}
